import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import ApprenticeshipSearch from '../components/ApprenticeshipSearch';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero Section - Apple Style */}
      <section className="pt-20 sm:pt-28 pb-12 sm:pb-20 px-4 sm:px-6 text-center bg-white">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-semibold tracking-tight text-[#1d1d1f] mb-3 sm:mb-4 leading-[1.05]">
            ApprenticeshipMatch
          </h1>
          <p className="text-2xl sm:text-3xl md:text-5xl font-semibold text-[#1d1d1f] mb-2 leading-[1.1]">
            Your future. Skilled. Matched.
          </p>
          <p className="text-lg sm:text-xl md:text-2xl text-[#86868b] mt-4 sm:mt-6 mb-6 sm:mb-8 leading-[1.3] px-4">
            Ireland's leading platform connecting talented young apprentices with top employers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-6 sm:mt-10 px-4">
            <Button
              onClick={() => navigate('/login')}
              className="bg-[#0071e3] text-white hover:bg-[#0077ed] text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 h-auto rounded-full font-normal w-full sm:w-auto"
              data-testid="hero-start-apprentice-btn"
            >
              Get started
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-transparent border-2 border-[#0071e3] text-[#0071e3] hover:bg-[#0071e3] hover:text-white text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 h-auto rounded-full font-normal w-full sm:w-auto"
            >
              Learn more
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Image Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80"
            alt="Team collaboration"
            className="w-full rounded-2xl sm:rounded-3xl shadow-2xl"
          />
        </div>
      </section>

      {/* Apprenticeship Search Section */}
      <ApprenticeshipSearch />

      {/* Stats Section - Apple Style Minimal */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#f5f5f7]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12 text-center">
            <div>
              <div className="text-5xl sm:text-6xl md:text-7xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">2,400+</div>
              <div className="text-base sm:text-xl text-[#86868b]">Apprentices onboarded</div>
            </div>
            <div>
              <div className="text-5xl sm:text-6xl md:text-7xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">180+</div>
              <div className="text-base sm:text-xl text-[#86868b]">Partner employers</div>
            </div>
            <div>
              <div className="text-5xl sm:text-6xl md:text-7xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">87%</div>
              <div className="text-base sm:text-xl text-[#86868b]">Interview success rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Product Card Style */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold text-center text-[#1d1d1f] mb-3 sm:mb-4">How it works</h2>
          <p className="text-xl sm:text-2xl text-center text-[#86868b] mb-12 sm:mb-20 px-4">Three simple steps to your dream apprenticeship</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm">
              <div className="h-48 sm:h-64 bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
                <div className="text-6xl sm:text-8xl font-bold text-[#0071e3]">1</div>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">Assess</h3>
                <p className="text-base sm:text-lg text-[#86868b] leading-relaxed">
                  Complete comprehensive assessments covering aptitude, work style, and interests.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm">
              <div className="h-48 sm:h-64 bg-gradient-to-br from-pink-50 to-orange-50 flex items-center justify-center">
                <div className="text-6xl sm:text-8xl font-bold text-[#0071e3]">2</div>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">Build</h3>
                <p className="text-base sm:text-lg text-[#86868b] leading-relaxed">
                  Create your professional CV with AI assistance that highlights your strengths.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm">
              <div className="h-48 sm:h-64 bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center">
                <div className="text-6xl sm:text-8xl font-bold text-[#0071e3]">3</div>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-2xl sm:text-3xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">Match</h3>
                <p className="text-base sm:text-lg text-[#86868b] leading-relaxed">
                  Get matched with employers looking for apprentices with your exact skills.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Apprentices Section */}
      <section className="py-24 px-6 bg-[#f5f5f7]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-5xl md:text-6xl font-semibold text-[#1d1d1f] mb-6 leading-tight">
              For apprentices
            </h2>
            <p className="text-2xl text-[#86868b] mb-8 leading-relaxed">
              Start your career with confidence using our comprehensive platform.
            </p>
            <ul className="space-y-4 text-xl text-[#1d1d1f]">
              <li className="flex items-start">
                <span className="mr-3">•</span>
                <span>Professional assessments that highlight your capabilities</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3">•</span>
                <span>AI-powered CV builder</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3">•</span>
                <span>Direct connections with verified employers</span>
              </li>
            </ul>
          </div>
          <div>
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80"
              alt="Apprentices"
              className="w-full rounded-3xl"
            />
          </div>
        </div>
      </section>

      {/* For Employers Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="order-2 md:order-1">
            <img
              src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80"
              alt="Employers"
              className="w-full rounded-3xl"
            />
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-5xl md:text-6xl font-semibold text-[#1d1d1f] mb-6 leading-tight">
              For employers
            </h2>
            <p className="text-2xl text-[#86868b] mb-8 leading-relaxed">
              Find the perfect apprentices faster with data-driven matching.
            </p>
            <ul className="space-y-4 text-xl text-[#1d1d1f]">
              <li className="flex items-start">
                <span className="mr-3">•</span>
                <span>Access to pre-assessed, ready-to-hire talent</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3">•</span>
                <span>Smart matching based on your requirements</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3">•</span>
                <span>Reduce time-to-hire by up to 60%</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section - Apple Style */}
      <section className="py-32 px-6 bg-[#f5f5f7] text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-semibold text-[#1d1d1f] mb-6">
            Ready to get started?
          </h2>
          <p className="text-2xl text-[#86868b] mb-10">
            Join thousands of apprentices and employers already finding their perfect match.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#0071e3] text-white hover:bg-[#0077ed] text-lg px-10 py-7 h-auto rounded-full font-normal"
            data-testid="cta-get-started-btn"
          >
            Get started today
          </Button>
        </div>
      </section>

      {/* Footer - Apple Style Minimal */}
      <footer className="bg-[#f5f5f7] border-t border-[#d2d2d7]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-4">Platform</h3>
              <ul className="space-y-2">
                <li><a href="/how-it-works" className="text-sm text-[#424245] hover:text-[#1d1d1f]">How It Works</a></li>
                <li><a href="/for-apprentices" className="text-sm text-[#424245] hover:text-[#1d1d1f]">For Apprentices</a></li>
                <li><a href="/for-employers" className="text-sm text-[#424245] hover:text-[#1d1d1f]">For Employers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="/about" className="text-sm text-[#424245] hover:text-[#1d1d1f]">About</a></li>
                <li><a href="/contact" className="text-sm text-[#424245] hover:text-[#1d1d1f]">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-4">Support</h3>
              <ul className="space-y-2">
                <li><a href="/faq" className="text-sm text-[#424245] hover:text-[#1d1d1f]">FAQ</a></li>
                <li><a href="/pricing" className="text-sm text-[#424245] hover:text-[#1d1d1f]">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="/privacy" className="text-sm text-[#424245] hover:text-[#1d1d1f]">Privacy</a></li>
                <li><a href="/terms" className="text-sm text-[#424245] hover:text-[#1d1d1f]">Terms</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-[#d2d2d7] text-center">
            <p className="text-sm text-[#86868b]">Copyright © 2026 ApprenticeshipMatch. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
